// Typed client for the website-builder API (server/pageRoutes.js).
//
// Public reads (no auth) back the live storefront at /g/:orgSlug; the gated
// CRUD methods back the site builder. All requests go through apiFetchJson,
// which adds CSRF + credentials on writes. Mirrors newsletterAdminService's
// shape so the builder feels like the email editor.
import { apiFetchJson } from './apiClient';
import type { PageDoc, SiteNav, SectionBlock, PageTheme, ProductExtraImage } from '../../shared/pageBlocks';
import { forEachLeaf } from '../../shared/pageBlocks';

// The canonical, per-product gallery customization (extra B2 images + order +
// per-variant hero) the user set on a product-embed detail block, surfaced so
// cards + the minishop share the same assets as the detail page. Keys are the
// buildMergedGallery opts.
export interface ProductGalleryConfig {
  showShopifyImages?: boolean;
  extraImages?: ProductExtraImage[];
  galleryOrder?: string[];
  variantHeroImages?: Record<string, string>;
}

// Extract per-product gallery configs from a doc's product-embed blocks CLIENT-
// side (same rule as the server extractor). Used by the editor so the preview's
// cards reflect the gallery the user just set — INCLUDING unsaved edits — without
// a round-trip. Overlaid on the fetched (cross-page) map by the editor.
export function docProductGalleries(doc: PageDoc): Record<string, ProductGalleryConfig> {
  const out: Record<string, ProductGalleryConfig> = {};
  forEachLeaf(doc, (leaf) => {
    const l = leaf as unknown as { type?: string; handle?: string; showShopifyImages?: boolean; extraImages?: ProductExtraImage[]; galleryOrder?: string[]; variantHeroImages?: Record<string, string> };
    if (l.type === 'product-embed' && l.handle
        && ((l.extraImages && l.extraImages.length) || (l.galleryOrder && l.galleryOrder.length) || l.variantHeroImages || l.showShopifyImages === false)) {
      out[l.handle] = {
        showShopifyImages: l.showShopifyImages,
        extraImages: l.extraImages,
        galleryOrder: l.galleryOrder,
        variantHeroImages: l.variantHeroImages,
      };
    }
  });
  return out;
}

// Fetch the per-product canonical gallery map. `orgSlug` present → that org's
// storefront; absent (the shadygram minishop / default store) → the default
// store. Always resolves (empty map on failure) so callers can render unchanged.
export async function fetchPublicProductGalleries(orgSlug?: string): Promise<Record<string, ProductGalleryConfig>> {
  const path = orgSlug
    ? `/api/pages/public/${encodeURIComponent(orgSlug)}/product-galleries`
    : '/api/storefront/default-product-galleries';
  try {
    const res = await apiFetchJson<{ success: boolean; galleries: Record<string, ProductGalleryConfig> }>(path);
    return res.galleries || {};
  } catch {
    return {};
  }
}

export interface SitePageRow {
  id?: number;
  slug: string;
  productHandle: string | null;
  /** Stable Shopify product ID (gid) — canonical product link, rename-proof.
   *  Null for non-product pages or legacy rows not yet backfilled. */
  productId: string | null;
  title: string | null;
  doc: PageDoc;
  published: number;
  updatedAt?: string;
  seeded?: boolean;
}

export interface PublicPageResult {
  doc: PageDoc;
  title?: string | null;
  fallback?: boolean;
}

// ── Public (storefront) ───────────────────────────────────────────────────────

// Fetch a published landing/custom page. Returns null on 404 (caller then tries
// the product fallback or renders 404).
export async function fetchPublicPage(orgSlug: string, slug = 'home'): Promise<PublicPageResult | null> {
  try {
    const res = await apiFetchJson<{ success: boolean; doc: PageDoc; title?: string }>(
      `/api/pages/public/${encodeURIComponent(orgSlug)}/${encodeURIComponent(slug)}`,
    );
    return { doc: res.doc, title: res.title };
  } catch {
    return null;
  }
}

// Fetch a product page: the builder page if published, else the template
// fallback the server returns (fallback:true).
export async function fetchPublicProductPage(orgSlug: string, handle: string): Promise<PublicPageResult | null> {
  try {
    const res = await apiFetchJson<{ success: boolean; doc: PageDoc; title?: string; fallback?: boolean }>(
      `/api/pages/public/${encodeURIComponent(orgSlug)}/product/${encodeURIComponent(handle)}`,
    );
    return { doc: res.doc, title: res.title, fallback: res.fallback };
  } catch {
    return null;
  }
}

// ── Gated editing (web-design permission) ─────────────────────────────────────

export async function listPages(): Promise<{ slug: string | null; pages: SitePageRow[] }> {
  const res = await apiFetchJson<{ success: boolean; slug: string | null; pages: SitePageRow[] }>('/api/pages');
  return { slug: res.slug, pages: res.pages };
}

export async function getPage(slug: string): Promise<SitePageRow> {
  const res = await apiFetchJson<{ success: boolean; page: SitePageRow }>(`/api/pages/${encodeURIComponent(slug)}`);
  return res.page;
}

export async function savePage(
  slug: string,
  doc: PageDoc,
  opts: { productHandle?: string | null; productId?: string | null; published?: boolean } = {},
): Promise<SitePageRow> {
  const res = await apiFetchJson<{ success: boolean; page: SitePageRow }>(`/api/pages/${encodeURIComponent(slug)}`, {
    method: 'PUT',
    body: JSON.stringify({ doc, productHandle: opts.productHandle ?? null, productId: opts.productId ?? null, published: opts.published }),
  });
  return res.page;
}

export async function createPage(doc: PageDoc, productHandle: string | null = null, productId: string | null = null): Promise<SitePageRow> {
  const res = await apiFetchJson<{ success: boolean; page: SitePageRow }>('/api/pages', {
    method: 'POST',
    body: JSON.stringify({ doc, productHandle, productId }),
  });
  return res.page;
}

// ── Direct Shopify product write-back (org-owner only on the server) ──────────

export interface ShopifyFieldsPatch {
  productId?: string;
  variantId?: string;
  title?: string;
  bodyHtml?: string;
  price?: string;
}

// Push edited product fields straight to the org's Shopify store. Throws with a
// human-readable message on failure (the server 403s non-owners, 400s when
// Shopify isn't configured, 502s a Shopify rejection).
export async function pushProductFields(handle: string, patch: ShopifyFieldsPatch): Promise<void> {
  await apiFetchJson(`/api/site/product/${encodeURIComponent(handle)}/shopify-fields`, {
    method: 'POST',
    body: JSON.stringify(patch),
  });
}

// Rename a product's handle ON SHOPIFY and re-point every internal reference
// (page docs, nav, footer). Returns the APPLIED handle (Shopify may suffix it on
// collision) plus how many pages were rewritten. Owner-only server-side.
export async function renameShopifyProductHandle(
  oldHandle: string,
  newHandle: string,
  productId: string,
): Promise<{ handle: string; pagesUpdated: number }> {
  const res = await apiFetchJson<{ success: boolean; handle: string; pagesUpdated: number }>(
    `/api/site/product/${encodeURIComponent(oldHandle)}/rename-handle`,
    { method: 'POST', body: JSON.stringify({ productId, newHandle }) },
  );
  return { handle: res.handle, pagesUpdated: res.pagesUpdated };
}

// Publish (active) or unpublish (draft) a Shopify product.
export async function setProductPublished(handle: string, productId: string, published: boolean): Promise<void> {
  await apiFetchJson(`/api/site/product/${encodeURIComponent(handle)}/shopify-publish`, {
    method: 'POST',
    body: JSON.stringify({ productId, published }),
  });
}

/** Media the server flags at publish time as not CDN-servable — every public
 * view streams these through the droplet (paid egress) until they're
 * re-uploaded/republished. */
export interface PublishMediaWarning {
  id: string;
  filename: string | null;
  file_size: number;
  reason: string;
}

export async function publishPage(
  slug: string,
  published: boolean,
): Promise<{ page: SitePageRow; mediaWarnings: PublishMediaWarning[] }> {
  const res = await apiFetchJson<{ success: boolean; page: SitePageRow; mediaWarnings?: PublishMediaWarning[] }>(
    `/api/pages/${encodeURIComponent(slug)}/publish`,
    {
      method: 'PUT',
      body: JSON.stringify({ published }),
    },
  );
  return { page: res.page, mediaWarnings: res.mediaWarnings ?? [] };
}

export async function deletePage(id: number): Promise<void> {
  await apiFetchJson(`/api/pages/${id}`, { method: 'DELETE' });
}

// Rename a page's slug (its public URL), addressed by id. The server rejects
// renaming 'home', a conflicting slug, or a malformed slug.
export async function renamePage(id: number, slug: string): Promise<SitePageRow> {
  const res = await apiFetchJson<{ success: boolean; page: SitePageRow }>(`/api/pages/${id}/slug`, {
    method: 'PUT',
    body: JSON.stringify({ slug }),
  });
  return res.page;
}

// Shared link-target partitioning for the inspector pickers (block links AND
// nav items). A product page, once opened/edited, is saved as a SitePageRow with
// a productHandle — so it must NOT also appear in the plain "Pages" list (that's
// the duplicate-in-dropdown bug). Products that HAVE a builder page sort to the
// top of the product list so the ones you've customized are easiest to find.
export function siteLinkTargets<P extends { handle: string }>(pages: SitePageRow[], products: P[]) {
  const customPages = pages.filter((p) => !p.productHandle);
  const handlesWithPage = new Set(pages.map((p) => p.productHandle).filter((h): h is string => !!h));
  // Stable sort: built products first, original order preserved within each group.
  const sortedProducts = [...products].sort(
    (a, b) => (handlesWithPage.has(b.handle) ? 1 : 0) - (handlesWithPage.has(a.handle) ? 1 : 0),
  );
  return { customPages, sortedProducts, hasPage: (handle: string) => handlesWithPage.has(handle) };
}

// Public product pages that have a published builder page, as
// { handle, slug, productId }. `productId` is the stable, rename-proof key used
// to resolve a product to its page; `handle` is the legacy fallback and sorts
// "opened" products to the top of dropdowns/grids; `slug` is the page's real URL
// (can diverge from the handle after a slug rename).
export interface ProductPageRef { handle: string; slug: string; productId?: string | null; }
export async function fetchPublicProductPages(orgSlug: string): Promise<ProductPageRef[]> {
  try {
    const res = await apiFetchJson<{ success: boolean; handles?: string[]; pages?: ProductPageRef[] }>(
      `/api/pages/public/${encodeURIComponent(orgSlug)}/product-pages`,
    );
    // Fall back to the legacy handles-only shape (slug === handle) if an older
    // server hasn't shipped the `pages` field yet.
    if (res.pages && res.pages.length) return res.pages.filter((p) => p && p.slug && (p.handle || p.productId));
    return (res.handles || []).map((h) => ({ handle: h, slug: h }));
  } catch {
    return [];
  }
}

// Storefront Shopify connection status for an org (public publishable config).
// `configured` true = the org has a Storefront token + domain, so the live shop
// works; the Admin (write) creds are owner-only and never exposed here.
export interface ShopifyConfigStatus { configured: boolean; domain?: string; apiVersion?: string; }
export async function fetchShopifyConfig(orgSlug: string): Promise<ShopifyConfigStatus> {
  try {
    const res = await apiFetchJson<{ configured: boolean; domain?: string; apiVersion?: string }>(
      `/api/pages/public/${encodeURIComponent(orgSlug)}/shopify-config`,
    );
    return { configured: !!res.configured, domain: res.domain, apiVersion: res.apiVersion };
  } catch {
    return { configured: false };
  }
}

// ── Site nav (editable top bar) ───────────────────────────────────────────────

export async function fetchPublicNav(orgSlug: string): Promise<SiteNav | null> {
  try {
    const res = await apiFetchJson<{ success: boolean; nav: SiteNav }>(
      `/api/pages/public/${encodeURIComponent(orgSlug)}/nav`,
    );
    return res.nav;
  } catch {
    return null;
  }
}

export async function getNav(): Promise<SiteNav> {
  const res = await apiFetchJson<{ success: boolean; nav: SiteNav }>('/api/site/nav');
  return res.nav;
}

export async function saveNav(nav: SiteNav): Promise<SiteNav> {
  const res = await apiFetchJson<{ success: boolean; nav: SiteNav }>('/api/site/nav', {
    method: 'PUT',
    body: JSON.stringify({ nav }),
  });
  return res.nav;
}

// ── Shared site footer (one pinned section across every page) ──────────────────

export async function fetchPublicFooter(orgSlug: string): Promise<SectionBlock | null> {
  try {
    const res = await apiFetchJson<{ success: boolean; footer: SectionBlock }>(
      `/api/pages/public/${encodeURIComponent(orgSlug)}/footer`,
    );
    return res.footer;
  } catch {
    return null;
  }
}

export async function getFooter(): Promise<SectionBlock> {
  const res = await apiFetchJson<{ success: boolean; footer: SectionBlock }>('/api/site/footer');
  return res.footer;
}

export async function saveFooter(footer: SectionBlock): Promise<SectionBlock> {
  const res = await apiFetchJson<{ success: boolean; footer: SectionBlock }>('/api/site/footer', {
    method: 'PUT',
    body: JSON.stringify({ footer }),
  });
  return res.footer;
}

// ── Shared site theme (site-wide colors/fonts/layout) ─────────────────────────

export async function fetchPublicTheme(orgSlug: string): Promise<PageTheme | null> {
  try {
    const res = await apiFetchJson<{ success: boolean; theme: PageTheme }>(
      `/api/pages/public/${encodeURIComponent(orgSlug)}/theme`,
    );
    return res.theme;
  } catch {
    return null;
  }
}

export async function getTheme(): Promise<PageTheme> {
  const res = await apiFetchJson<{ success: boolean; theme: PageTheme }>('/api/site/theme');
  return res.theme;
}

export async function saveTheme(theme: PageTheme): Promise<PageTheme> {
  const res = await apiFetchJson<{ success: boolean; theme: PageTheme }>('/api/site/theme', {
    method: 'PUT',
    body: JSON.stringify({ theme }),
  });
  return res.theme;
}

// ── Timed mailing-list promo popup (one config per org) ───────────────────────

export interface PopupConfig {
  enabled: boolean;
  headline: string;
  subtext: string;
  /** Seconds after page load before the popup appears. */
  delaySeconds: number;
}

// Public read: the popup config for the storefront, only present when enabled
// (popup is null otherwise). orgName lets the popup/newsletter copy name the org.
export async function fetchPublicPopup(orgSlug: string): Promise<{ orgName: string | null; popup: PopupConfig | null }> {
  try {
    const res = await apiFetchJson<{ success: boolean; orgName: string | null; popup: PopupConfig | null }>(
      `/api/pages/public/${encodeURIComponent(orgSlug)}/popup`,
    );
    return { orgName: res.orgName ?? null, popup: res.popup ?? null };
  } catch {
    return { orgName: null, popup: null };
  }
}

export async function getPopup(): Promise<PopupConfig> {
  const res = await apiFetchJson<{ success: boolean; popup: PopupConfig }>('/api/site/popup');
  return res.popup;
}

export async function savePopup(popup: PopupConfig): Promise<PopupConfig> {
  const res = await apiFetchJson<{ success: boolean; popup: PopupConfig }>('/api/site/popup', {
    method: 'PUT',
    body: JSON.stringify({ popup }),
  });
  return res.popup;
}
